/* eslint-disable react-hooks/immutability -- this confirmation step mutates the existing settings draft before save. */
import React, {useCallback, useEffect, useReducer, useState} from "react";
import styled from "styled-components";
import {FileUtils} from "../../../../utils/file/FileUtils";
import {XyiconFeature} from "../../../../generated/api/base";
import {Space} from "../../../../data/models/Space";
import type {SpaceFileInsertionInfo} from "../../../../generated/api/base";
import type {SpaceVersion} from "../../../../data/models/SpaceVersion";
import {Functions} from "../../../../utils/function/Functions";
import type {Type} from "../../../../data/models/Type";
import type {SpaceTool} from "../../../modules/space/spaceeditor/logic3d/features/tools/Tools";
import {SpaceEditorMode, isSpaceNamePresentInAnotherSpaceVersion} from "../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import type {IBasicPropsFor3DTweaking} from "../../../modules/space/SpaceView";
import {useAppStore} from "../../../../StateManager";
import {SpaceEditorV5} from "../spaceeditor/SpaceEditorV5";
import {ConfirmWindowV5} from "../../popup/ConfirmWindowV5";
import type {ISpaceItemModel} from "../../../../data/models/Model";
import {radius} from "../../styles/styles";
import {Constants} from "../../../modules/space/spaceeditor/logic3d/Constants";
import {TimeUtils} from "../../../../utils/TimeUtils";
import type {IOnePagePDFDoc} from "./CreateSpacePanelV5";
import {PDFListV5} from "./PDFListV5";
import {LargePDFWarning} from "./LargePDFWarning";

const getEmptyArray = (): ISpaceItemModel[] => [];

interface ISpaceConfirmSettingsV5Props extends IBasicPropsFor3DTweaking {
	readonly selectedVersion: SpaceVersion;
	readonly spaceType: Type;
	readonly pdfFiles: {
		originalFiles: File[];
		onePagePDFDocs: IOnePagePDFDoc[][]; // OriginalPDFFiles split by pages. pdfFiles.onePagePDFDocs[3] is an array of PDF documents that is generated by the pages of pdpfFiles.originalFiles[3]
	};
	readonly applySpaceUnitsPerMeterToAllSpaces: (spaceUnitsPerMeter: number) => void;
	readonly forceUpdate: () => void; // needed to update the "next" button (enabled/disabled)
}

interface ILargePDFObject {
	showTooLargePDFWarning: boolean;
	renderLargePDF: boolean;
	space: Space | null;
}

export const SpaceConfirmSettingsV5 = (props: ISpaceConfirmSettingsV5Props) => {
	const {selectedVersion, spaceType, pdfFiles, applySpaceUnitsPerMeterToAllSpaces, forceUpdate} = props;
	const appState = useAppStore((state) => state.appState);
	const {spaceViewRenderer} = appState.app;
	const [, forceUpdateLocal] = useReducer((x) => x + 1, 0);
	const [activePDFFile, setActivePDFFile] = useState<IOnePagePDFDoc>(pdfFiles.onePagePDFDocs[0][0]);
	const [activeToolId, setActiveToolId] = useState<SpaceTool>("pan");
	const [isPDFLoaded, setIsPDFLoaded] = useState<boolean>(false);
	const [largePDFObject, setLargePDFObject] = useState<ILargePDFObject>({
		renderLargePDF: false,
		showTooLargePDFWarning: false,
		space: null,
	});

	const getFileContentOfPDF = (pdf: IOnePagePDFDoc) => {
		return pdf.doc.save();
	};

	const getFileSizeOfBytesInMB = (bytes: Uint8Array) => {
		return bytes.length / 1024 / 1024;
	};

	const getMode = useCallback(
		(activePDF: IOnePagePDFDoc = activePDFFile) => {
			const spaceName = activePDF.spaceName;

			return isSpaceNamePresentInAnotherSpaceVersion(appState.actions, spaceName) ? SpaceEditorMode.ALIGN : SpaceEditorMode.SET_SCALE;
		},
		[activePDFFile, appState.actions],
	);

	const mode = getMode();

	const loadSpace = useCallback(
		async (space: Space) => {
			await TimeUtils.waitForNextFrame();
			await TimeUtils.waitForNextFrame();
			spaceViewRenderer.populateData(space);
		},
		[spaceViewRenderer],
	);

	const loadActivePDF = useCallback(
		async (newActivePDF: IOnePagePDFDoc = activePDFFile) => {
			let space: Space;

			const getActivePDFURL = (pdfData: Uint8Array) => {
				return FileUtils.createURLFromData(pdfData as BlobPart);
			};

			const mode = getMode(newActivePDF);

			if (mode === SpaceEditorMode.ALIGN) {
				const spaceName = newActivePDF.spaceName;
				const spaces = appState.actions.getList<Space>(XyiconFeature.Space);

				space = spaces.find((space: Space) => space.name === spaceName);
			}

			const pdfContent = await getFileContentOfPDF(newActivePDF);

			const spaceFileData = {
				spaceFileID: null as string,
				sourceFile: getActivePDFURL(pdfContent),
				thumbnailFile: newActivePDF.thumbnail,
				settings: newActivePDF.insertionInfo ? {insertionInfo: newActivePDF.insertionInfo} : (null as {insertionInfo: SpaceFileInsertionInfo}),
				spaceVersionID: selectedVersion.id,
			};

			if (!space) {
				space = new Space(
					{
						spaceID: null,
						spaceRefID: null,
						spaceTypeID: spaceType.id,
						portfolioID: appState.activePortfolioId,
						name: newActivePDF.spaceName,
						unitsPerMeter: null,
						fieldData: null,
						settings: null,
						spaceFiles: [spaceFileData],
					},
					appState,
				);
			} else {
				space.addTemporarySpaceFile(spaceFileData);
			}

			if (newActivePDF.insertionInfo) {
				space.newestSpaceFile.setInsertionInfo(newActivePDF.insertionInfo);
			}

			const sizeInMB = getFileSizeOfBytesInMB(pdfContent);

			setIsPDFLoaded(true);

			if (sizeInMB >= Constants.SIZE.PDF_WARNING_THRESHOLD) {
				setLargePDFObject({
					showTooLargePDFWarning: true,
					renderLargePDF: false,
					space,
				});
			} else {
				await loadSpace(space);
			}
		},
		[appState, activePDFFile, getMode, selectedVersion.id, spaceType.id, loadSpace],
	);

	const onSelect = async (originalPDFIndex: number, originalPageNumber: number) => {
		const setNewState = async () => {
			setIsPDFLoaded(false);
			setActiveTool("pan");
			const newActivePDF = pdfFiles.onePagePDFDocs[originalPDFIndex][originalPageNumber];

			setActivePDFFile(newActivePDF);
			setLargePDFObject({
				renderLargePDF: false,
				showTooLargePDFWarning: false,
				space: null,
			});
			await loadActivePDF(newActivePDF);
		};

		if (!largePDFObject.showTooLargePDFWarning && activePDFFile.spaceId && !activePDFFile.isAlignmentConfirmed) {
			const confirmed = await ConfirmWindowV5.open("Are you sure you want to switch without confirming your alignment first?", "Please confirm", {
				ok: "Yes",
				cancel: "No",
			});

			if (confirmed) {
				await setNewState();
			}
		} else {
			await setNewState();
		}
	};

	const onRenderPDFClick = async () => {
		setLargePDFObject((prevLargePDFObject) => ({
			showTooLargePDFWarning: false,
			renderLargePDF: true,
			space: prevLargePDFObject.space,
		}));

		activePDFFile.isSkipped = false;
		const mode = getMode(activePDFFile);

		if (mode === SpaceEditorMode.ALIGN) {
			activePDFFile.isAlignmentConfirmed = false;
		} else {
			activePDFFile.spaceUnitsPerMeter = null;
		}
		forceUpdate();
		forceUpdateLocal();

		await loadSpace(largePDFObject.space);
	};

	const onSkipClick = () => {
		const mode = getMode(activePDFFile);
		activePDFFile.isSkipped = true;

		if (mode === SpaceEditorMode.ALIGN) {
			confirmAlignment(largePDFObject.space.newestSpaceFile.insertionInfo);
		} else {
			activePDFFile.spaceUnitsPerMeter = 1; // just a default value, usually too large, so it's quite obvious that it's "wrong"
			forceUpdate();
		}
	};

	const setScale = (spaceUnitsPerMeter: number, applyToAllSpaces: boolean = false) => {
		if (applyToAllSpaces) {
			applySpaceUnitsPerMeterToAllSpaces(spaceUnitsPerMeter);
		} else {
			activePDFFile.spaceUnitsPerMeter = spaceUnitsPerMeter;
			forceUpdate();
			forceUpdateLocal();
		}
	};

	const confirmAlignment = (insertionInfo: SpaceFileInsertionInfo) => {
		const activePDF = activePDFFile;

		activePDF.insertionInfo = insertionInfo;
		activePDF.isAlignmentConfirmed = true;

		const space = appState.actions.getFeatureItemById(activePDF.spaceId, XyiconFeature.Space) as Space;

		space.newestSpaceFile.setInsertionInfo(insertionInfo);

		forceUpdate();
		forceUpdateLocal();
	};

	const setActiveTool = (id: SpaceTool) => {
		setActiveToolId(id);
	};

	useEffect(() => {
		const fetchData = async () => {
			await loadActivePDF();
		};

		fetchData();
	}, [loadActivePDF]);

	const replacementText = mode === SpaceEditorMode.ALIGN ? "alignment" : "scale";

	return (
		<SpaceScaleSettingsStyled className="SpaceScaleSettings hbox">
			<PDFListV5
				selectedVersion={selectedVersion}
				pdfFiles={pdfFiles}
				mode="SetScaleOrAlign"
				activePDFFile={activePDFFile}
				onSelect={onSelect}
				forceUpdate={forceUpdate}
			/>
			{largePDFObject.showTooLargePDFWarning && !largePDFObject.renderLargePDF ? (
				<LargePDFWarning
					replacementText={replacementText}
					onRenderPDFClick={onRenderPDFClick}
					onSkipClick={onSkipClick}
					isSkipped={activePDFFile.isSkipped}
				/>
			) : (
				isPDFLoaded && (
					<SpaceEditorV5
						spaceViewRenderer={appState.app.spaceViewRenderer}
						mode={mode}
						setScale={setScale}
						confirmAlignment={confirmAlignment}
						setActiveTool={setActiveTool}
						activeToolId={activeToolId}
						selectionToolType="selection"
						focusItems={Functions.emptyFunction}
						onAddCatalogClick={Functions.emptyFunction}
						onDuplicateCatalogClick={Functions.emptyFunction}
						toggleDetailsPanel={Functions.emptyFunction}
						onOpenPanelClick={Functions.emptyFunction}
						getFocusedItems={getEmptyArray}
					/>
				)
			)}
		</SpaceScaleSettingsStyled>
	);
};

const SpaceScaleSettingsStyled = styled.div`
	position: relative;
	height: calc(100vh - 300px);
	margin-left: initial;
	margin-right: initial;
	position: relative;
	width: 100%;
	height: 100%;
	overflow: hidden;

	.PDFList {
		width: 100%;
		max-width: 200px;
		overflow: auto;
	}

	.pdfContainer {
		cursor: pointer;
		margin: 5px;
		border-radius: ${radius.sm};
	}
`;
